import { useMemo } from "react";
import type { ClientConfiguration } from "../../../../../types/clientConfig";
import {
  isFemaleCycleSectionEnabledFromConfig,
  isSectionEffectivelyVisible,
} from "../../../../../utils/client-configuration-helpers";
import { SECTION_RADIO_CONFIG, TRACKER_KEYS } from "./utils";

function getActiveTrackerCount(clientConfig: ClientConfiguration) {
  const items = clientConfig?.sections?.tracking?.items || {};
  return TRACKER_KEYS.filter(
    (trackerKey) =>
      items[trackerKey]?.coachRequired || items[trackerKey]?.clientEnabled
  ).length;
}

export const useVisibleSections = ({
  clientConfig,
}: {
  clientConfig: ClientConfiguration;
}) => {
  const isTrainingVisible = useMemo(
    () => isSectionEffectivelyVisible(clientConfig?.sections?.training),
    [clientConfig]
  );
  const isTrainingClientOverride = useMemo(() => {
    const trainingSection = clientConfig?.sections?.training;
    return trainingSection?.mode === "hidden" && !!trainingSection?.clientOptIn;
  }, [clientConfig]);

  const isGoalsVisible = useMemo(
    () => isSectionEffectivelyVisible(clientConfig?.sections?.goals),
    [clientConfig]
  );
  const isGoalsClientOverride = useMemo(() => {
    const goalsSection = clientConfig?.sections?.goals;
    return goalsSection?.mode === "hidden" && !!goalsSection?.clientOptIn;
  }, [clientConfig]);

  const isYourPlansVisible = useMemo(
    () => isSectionEffectivelyVisible(clientConfig?.sections?.yourPlans),
    [clientConfig]
  );
  const isYourPlansClientOverride = useMemo(() => {
    const yourPlansSection = clientConfig?.sections?.yourPlans;
    return (
      yourPlansSection?.mode === "hidden" && !!yourPlansSection?.clientOptIn
    );
  }, [clientConfig]);

  const isFemaleCycleEnabled = useMemo(
    () => isFemaleCycleSectionEnabledFromConfig(clientConfig),
    [clientConfig]
  );
  const isFemaleCycleClientOverride = useMemo(() => {
    const femaleCycleSection = clientConfig?.sections?.femaleCycleTracking;
    return (
      femaleCycleSection?.mode === "hidden" && !!femaleCycleSection?.clientOptIn
    );
  }, [clientConfig]);

  const activeTrackerCount = useMemo(
    () => getActiveTrackerCount(clientConfig),
    [clientConfig]
  );

  const isTrackingEnabled = useMemo(() => {
    const mode = clientConfig?.sections?.tracking?.mode;
    return !!mode && mode !== "hidden";
  }, [clientConfig]);

  const sectionClientOverrides = useMemo(() => {
    const sections = clientConfig?.sections ?? {};
    return Object.fromEntries(
      SECTION_RADIO_CONFIG.map((block) => [
        block.key,
        sections[block.key]?.mode === "hidden" &&
          !!sections[block.key]?.clientOptIn,
      ])
    );
  }, [clientConfig]);

  return {
    isTrainingVisible,
    isTrainingClientOverride,
    isGoalsVisible,
    isGoalsClientOverride,
    isYourPlansVisible,
    isYourPlansClientOverride,
    isFemaleCycleEnabled,
    isFemaleCycleClientOverride,
    activeTrackerCount,
    isTrackingEnabled,
    sectionClientOverrides,
  };
};
